import React, { useEffect, useState } from "react";
import { Form, Button, SelectPicker } from "rsuite";
import { toast } from "react-toastify";
import "./AddUser.css";
import { addUser } from "../services/apiService";

const AddUser = () => {
    const [addUserData, setAddUserData] = useState({
        firstName: "",
        lastName: "",
        userEmail: "",
        password: "",
        userMobile: "",
        userType: "", // Added userType field
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const userTypeList = [
        { user_type_id: 1, userType: "Admin" },
        // {user_type_id: 2, userType: "Finance"},
        // {user_type_id: 4, userType: "Retailer"}
    ]

    const validateForm = (): { valid: boolean; message: string } => {
        if (!addUserData.firstName.trim()) {
            return { valid: false, message: "Please enter first name" };
        }
        if (!addUserData.userEmail.trim()) {
            return { valid: false, message: "Please enter email address" };
        }
        if (!addUserData.password.trim()) {
            return { valid: false, message: "Please enter password" };
        }
        if (!addUserData.userMobile.trim()) {
            return { valid: false, message: "Please enter mobile number" };
        }
        if (!addUserData.userType) {
            return { valid: false, message: "Please select user type" };
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(addUserData.userEmail)) {
            return { valid: false, message: "Please enter a valid email address" };
        }

        // Mobile number validation (assuming 10 digits)
        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(addUserData.userMobile)) {
            return { valid: false, message: "Please enter a valid 10-digit mobile number" };
        }

        // Password validation
        const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{8,}$/;
        if (!strongPasswordRegex.test(addUserData.password)) {
            return {
                valid: false,
                message: "Password must be at least 8 characters long, at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
            };
        }

        return { valid: true, message: "" };
    };

    const onSubmit = async (e: React.MouseEvent) => {
        e.preventDefault();

        const { valid, message } = validateForm();

        if (!valid) {
            toast.error(message);
            return;
        }

        setIsLoading(true);
        try {
            let payload = {
                firstName: addUserData.firstName.trim(),
                lastName: addUserData.lastName.trim(),
                userEmail: addUserData.userEmail.trim(),
                password: addUserData.password.trim(),
                userMobile: addUserData.userMobile.trim(),
                userType: addUserData.userType, // Include userType in payload
            };

            console.log("Payload being sent:", payload);

            const response = await addUser(payload);
            console.log("Add User response:", response);
            console.log(response?.code, "response?.data?.code")
            if (response?.code === 200 || response?.status === 200) {
                toast.success(response?.message || "User added successfully!");
                resetForm();
            } else {
                const errorMessage = response?.message || "Failed to add user";
                toast.error(errorMessage);
            }
        } catch (error: any) {
            console.error("Add User Error:", error);

            // Handle different types of errors
            if (error.response?.message) {
                toast.error(error?.response?.message);
            } else if (error?.message) {
                toast.error(error?.message);
            } else {
                toast.error("An error occurred while adding user");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setAddUserData({
            firstName: "",
            lastName: "",
            userEmail: "",
            password: "",
            userMobile: "",
            userType: "", // Reset userType as well
        });
    };

    const handleInput = (value: string, type: string) => {
        if (type === "userMobile") {
            // Only allow numbers for mobile
            const numericValue = value?.replace(/[^0-9]/g, '');
            setAddUserData((prev) => ({ ...prev, [type]: numericValue }));
        } else if (type === "userEmail") {
            // Allow email format
            setAddUserData((prev) => ({ ...prev, [type]: value }));
        } else {
            setAddUserData((prev) => ({ ...prev, [type]: value }));
        }
    };

    return (
        <div className="add-user-page">
            <div className="qr-generation-container">
                <div className="form-header">
                    <h2 className="welcome-title">Add User</h2>
                </div>

                <Form className="qr-generation-form">
                    {/* First row - 3 inputs */}
                    <div className="form-rows">
                        <Form.Group controlId="userType" className="form-group">
                            <Form.ControlLabel className="form-label">
                                User Type <span className="required">*</span>
                            </Form.ControlLabel>

                            <SelectPicker
                                data={userTypeList?.map((item: any) => ({
                                    label: item.userType,
                                    value: item.user_type_id
                                }))}
                                labelKey="label"
                                valueKey="value"
                                style={{ width: '100%' }}
                                placeholder="Choose User Type"
                                onChange={(value: any) => {
                                    handleInput(value || "", "userType");
                                }}
                                value={addUserData.userType}
                                className="form-select"
                            // cleanable={false}
                            />
                        </Form.Group>

                        <Form.Group controlId="firstName" className="form-group">
                            <Form.ControlLabel className="form-label">
                                First Name <span className="required">*</span>
                            </Form.ControlLabel>
                            <Form.Control
                                name="firstName"
                                type="text"
                                value={addUserData.firstName}
                                onChange={(value: string) => handleInput(value || "", "firstName")}
                                placeholder="Enter first name"
                                className="form-input"
                                style={{ width: 'auto' }}
                            />
                        </Form.Group>

                        <Form.Group controlId="lastName" className="form-group">
                            <Form.ControlLabel className="form-label">
                                Last Name
                            </Form.ControlLabel>
                            <Form.Control
                                name="lastName"
                                type="text"
                                value={addUserData.lastName}
                                onChange={(value: string) => handleInput(value || "", "lastName")}
                                placeholder="Enter last name (optional)"
                                className="form-input"
                                style={{ width: 'auto' }}
                            />
                        </Form.Group>

                    </div>

                    {/* Second row - 2 inputs, left aligned */}
                    <div className="form-rows" style={{ justifyContent: 'flex-start', }}>
                        <Form.Group controlId="userMobile" className="form-group">
                            <Form.ControlLabel className="form-label">
                                Mobile Number <span className="required">*</span>
                            </Form.ControlLabel>
                            <Form.Control
                                name="userMobile"
                                type="text"
                                value={addUserData.userMobile}
                                onChange={(value: string) => handleInput(value || "", "userMobile")}
                                placeholder="Enter mobile number"
                                className="form-input"
                                maxLength={10}
                                style={{ width: 'auto' }}
                            />
                        </Form.Group>

                        <Form.Group controlId="userEmail" className="form-group">
                            <Form.ControlLabel className="form-label">
                                Email Address <span className="required">*</span>
                            </Form.ControlLabel>
                            <Form.Control
                                name="userEmail"
                                type="email"
                                value={addUserData.userEmail}
                                onChange={(value: string) => handleInput(value || "", "userEmail")}
                                placeholder="Enter email address"
                                className="form-input"
                                style={{ width: 'auto' }}
                            />
                        </Form.Group>

                        <Form.Group controlId="password" className="form-group">
                            <Form.ControlLabel className="form-label">
                                Password <span className="required">*</span>
                            </Form.ControlLabel>
                            <Form.Control
                                name="password"
                                // type="password"
                                value={addUserData.password}
                                onChange={(value: string) => handleInput(value || "", "password")}
                                placeholder="Enter password"
                                className="form-input"
                                style={{ width: 'auto' }}
                            />
                        </Form.Group>
                    </div>

                    <Button
                        appearance="primary"
                        onClick={onSubmit}
                        disabled={isLoading}
                        className="generate-button"
                    >
                        {isLoading ? "Adding User..." : "Add User"}
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default AddUser;